import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2, Youtube, Video, Tag } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const PREDEFINED_TAGS = [
  'Glaube', 'Familie', 'Gebet', 'Heilung', 'Lobpreis',
  'Hoffnung', 'Liebe', 'Vergebung', 'Gemeinschaft', 'Mission',
  'Bibelstudie', 'Ermutigung', 'Weihnachten', 'Ostern', 'Taufe'
];

const emptyForm = {
  title: '',
  description: '',
  speaker: 'ICG Hannover',
  date: new Date().toISOString().split('T')[0],
  platform: 'youtube',
  video_url: '',
  duration: '',
  tags: [],
};

const AdminSermonsTab = () => {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [customTag, setCustomTag] = useState('');

  const getAuthHeaders = (includeJson = true) => {
    const token = localStorage.getItem('admin_token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (includeJson) headers['Content-Type'] = 'application/json';
    return headers;
  };

  useEffect(() => {
    fetchSermons();
  }, []);

  const fetchSermons = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/sermons`);
      const data = await res.json();
      setSermons(data || []);
    } catch (err) {
      console.error('Error fetching sermons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setCustomTag('');
    setShowForm(true);
  };

  const handleEdit = (sermon) => {
    setEditingId(sermon.id);
    setForm({
      title: sermon.title || '',
      description: sermon.description || '',
      speaker: sermon.speaker || 'ICG Hannover',
      date: sermon.date ? sermon.date.split('T')[0] : new Date().toISOString().split('T')[0],
      platform: sermon.platform || 'youtube',
      video_url: sermon.video_url || '',
      duration: sermon.duration || '',
      tags: sermon.tags || [],
    });
    setCustomTag('');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Diese Predigt wirklich loeschen?')) return;
    try {
      await fetch(`${API_BASE}/sermons/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      setSermons(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      alert('Fehler beim Loeschen');
    }
  };

  const toggleTag = (tag) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const addCustomTag = () => {
    const trimmed = customTag.trim();
    if (trimmed && !form.tags.includes(trimmed)) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, trimmed] }));
    }
    setCustomTag('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, date: new Date(form.date).toISOString() };
      const url = editingId ? `${API_BASE}/sermons/${editingId}` : `${API_BASE}/sermons`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Fehler beim Speichern');
        setSaving(false);
        return;
      }
      await fetchSermons();
      setShowForm(false);
      setEditingId(null);
    } catch (err) {
      alert('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getThumbnail = (sermon) => {
    if (sermon.thumbnail_url) return sermon.thumbnail_url;
    if (sermon.video_id && sermon.platform === 'youtube') {
      return `https://img.youtube.com/vi/${sermon.video_id}/mqdefault.jpg`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">
          Predigten verwalten ({sermons.length})
        </h2>
        <button
          onClick={handleCreate}
          data-testid="admin-add-sermon-btn"
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Neue Predigt
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6" data-testid="sermon-form">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-900">
              {editingId ? 'Predigt bearbeiten' : 'Neue Predigt hinzufuegen'}
            </h3>
            <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                <input type="text" required value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  data-testid="sermon-form-title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. Glaube in schwierigen Zeiten" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prediger</label>
                <input type="text" value={form.speaker}
                  onChange={e => setForm({ ...form, speaker: e.target.value })}
                  data-testid="sermon-form-speaker"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung *</label>
              <textarea required rows={2} value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                data-testid="sermon-form-description"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube-URL *</label>
                <input type="url" required value={form.video_url}
                  onChange={e => setForm({ ...form, video_url: e.target.value })}
                  data-testid="sermon-form-url"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://youtube.com/watch?v=..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
                <input type="date" value={form.date}
                  onChange={e => setForm({ ...form, date: e.target.value })}
                  data-testid="sermon-form-date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dauer</label>
                <input type="text" value={form.duration}
                  onChange={e => setForm({ ...form, duration: e.target.value })}
                  data-testid="sermon-form-duration"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="z.B. 45 Min" />
              </div>
            </div>

            {/* Tags Section */}
            <div data-testid="sermon-form-tags-section">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="w-4 h-4 inline-block mr-1 -mt-0.5" />
                Kategorien / Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {PREDEFINED_TAGS.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    data-testid={`tag-option-${tag}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      form.tags.includes(tag)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              {/* Custom tag input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customTag}
                  onChange={e => setCustomTag(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
                  data-testid="sermon-form-custom-tag"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Eigenen Tag hinzufuegen..."
                />
                <button type="button" onClick={addCustomTag}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                  +
                </button>
              </div>
              {/* Selected tags display */}
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {form.tags.map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {tag}
                      <button type="button" onClick={() => toggleTag(tag)} className="hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} data-testid="sermon-form-save"
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all flex items-center gap-2 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all">
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {sermons.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Video className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">Noch keine Predigten vorhanden</p>
            <p className="text-sm mt-1">Fuege deine erste Predigt mit YouTube-Link hinzu.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100" data-testid="sermons-list">
            {sermons.map((sermon) => (
              <div key={sermon.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                data-testid={`sermon-row-${sermon.id}`}>
                {/* Thumbnail */}
                <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                  {getThumbnail(sermon) ? (
                    <img src={getThumbnail(sermon)} alt={sermon.title}
                      className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Youtube className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{sermon.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {sermon.speaker} &middot; {sermon.date ? new Date(sermon.date).toLocaleDateString('de-DE') : ''} &middot; {sermon.duration || ''}
                  </p>
                  {sermon.tags && sermon.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {sermon.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => handleEdit(sermon)} data-testid={`edit-sermon-${sermon.id}`}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Bearbeiten">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(sermon.id)} data-testid={`delete-sermon-${sermon.id}`}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Loeschen">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSermonsTab;
